// AddAccountModal.tsx
import styles from "./styles.module.css";
import { closeModal } from "src/store/slices/modalSlice";
import { useDispatch } from "react-redux";
import { Button, BUTTONTYPE } from "src/components/atoms/Button";
import closeIcon from "../../../../assets/icons/closeGray.svg";
import { useFormik } from "src/hooks/useFormik";
import * as Yup from "yup";
import { Select } from "src/components/atoms/Select";
import { Input } from "src/components/atoms/Input";
import { useCallback, useMemo, useState } from "react";
import useBanks from "src/hooks/useBanks";
import { useClientCompanies } from "src/hooks/useClientCompanies";
import { useAppSelector } from "src/store/hooks";
import { apiClient } from "src/api/client";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";

type Option = {
  id: string | number;
  label: string;
};

type FormValues = {
  accountName: string;
  accountBank: Option | null;
  accountCompany: Option | null;
  accountType: Option | null;
  accountNumber: string;
};

interface AddAccountModalProps {
  onClose?: () => void;
  onSubmit?: (data: FormValues) => void;
  clientId?: number;
}

const validationSchema = Yup.object({
  accountName: Yup.string()
    .required("Account name is required")
    .min(2, "Account name must be at least 2 characters"),
  accountBank: Yup.object().nullable().required("Bank is required"),
  accountCompany: Yup.object().nullable(),
  accountType: Yup.object().required("Account type is required"),
  accountNumber: Yup.string()
    .required("Account number is required")
    .min(4, "Account number must be at least 4 characters"),
});

export const AddAccountModal: React.FC<AddAccountModalProps> = ({
  onClose,
  onSubmit,
  clientId,
}) => {
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankTypes, setBankTypes] = useState<Option[]>([]);
  const [bankTypesLoading, setBankTypesLoading] = useState(false);

  const authUser = useAppSelector((state) => state.auth.user);
  const effectiveClientId = clientId ?? authUser?.id;

  const { banks, loading: banksLoading, error: banksError } = useBanks();
  const {
    companies,
    loading: companiesLoading,
    error: companiesError,
  } = useClientCompanies(clientId);

  const handleClose = () => {
    if (onClose) onClose();
    else dispatch(closeModal({ type: "addAccountModal" }));
  };

  const handleSubmit = async (values: FormValues) => {
    if (!effectiveClientId) {
      setError("Client ID is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (onSubmit) {
        await onSubmit(values);
      } else {
        const bankId = values.accountBank?.id
          ? Number(values.accountBank.id)
          : null;
        const isPersonalAccount = values.accountCompany?.id === PERSONAL_ACCOUNT_ID;
        const companyId = values.accountCompany?.id && !isPersonalAccount
          ? Number(values.accountCompany.id)
          : null;
        const apiData: any = {
          account_name: values.accountName.trim(),
          account_number: values.accountNumber.trim(),
          currency: "USD",
          account_type: values.accountType?.id,
          client_id: effectiveClientId,
        };
        if (bankId) apiData.bank_id = bankId;
        if (companyId) apiData.company_id = companyId;
        await apiClient.createAccount(apiData);
      }
      handleClose();
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      accountName: "",
      accountBank: null as Option | null,
      accountCompany: null as Option | null,
      accountType: null as Option | null,
      accountNumber: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const fetchBankTypes = useCallback(async (bankId: number, hasCompany: boolean) => {
    setBankTypesLoading(true);
    setBankTypes([]);
    try {
      const data = await apiClient.getBankTypes(bankId);
      const filtered = data.filter((t) => t.is_personal === !hasCompany);
      setBankTypes(
        filtered.map((t) => ({
          id: t.type,
          label: t.type.charAt(0).toUpperCase() + t.type.slice(1),
        }))
      );
    } catch {
      setBankTypes([]);
    } finally {
      setBankTypesLoading(false);
    }
  }, []);

  const itemsBank = useMemo(
    () =>
      banks.map((bank) => ({
        id: bank.id,
        label: bank.name,
      })),
    [banks],
  );

  const PERSONAL_ACCOUNT_ID = "personal";

  const itemsCompany = useMemo(
    () => [
      { id: PERSONAL_ACCOUNT_ID, label: "Personal account" },
      ...companies.map((company) => ({
        id: company.id,
        label: company.name,
      })),
    ],
    [companies],
  );

  const handleBankSelect = useCallback(
    (id: string | number | null) => {
      const selected = itemsBank.find((item) => item.id === id) ?? null;
      formik.setFieldTouched("accountBank", true, false);
      formik.setFieldValue("accountBank", selected);
      formik.setFieldValue("accountCompany", null);
      formik.setFieldValue("accountType", null);
      setBankTypes([]);
      if (selected) {
        fetchBankTypes(Number(selected.id), false);
      }
    },
    [formik, itemsBank, fetchBankTypes],
  );

  const handleCompanySelect = useCallback(
    (id: string | number | null) => {
      const selected = itemsCompany.find((item) => item.id === id) ?? null;
      formik.setFieldTouched("accountCompany", true, false);
      formik.setFieldValue("accountCompany", selected);
      formik.setFieldValue("accountType", null);
      const isPersonal = !selected || selected.id === PERSONAL_ACCOUNT_ID;
      const bankId = formik.values.accountBank?.id;
      if (bankId) {
        fetchBankTypes(Number(bankId), !isPersonal);
      }
    },
    [formik, itemsCompany, fetchBankTypes],
  );

  const handleTypeSelect = useCallback(
    (id: string | number | null) => {
      const selected = bankTypes.find((item) => item.id === id) ?? null;
      formik.setFieldTouched("accountType", true, false);
      formik.setFieldValue("accountType", selected);
    },
    [formik, bankTypes],
  );

  const getError = (
    touched: boolean | undefined,
    error: any,
  ): string | undefined => {
    if (touched && error) {
      return error as string;
    }
    return undefined;
  };

  const hasBank = !!formik.values.accountBank;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h3>Add account</h3>
          <Button
            text=""
            icon={closeIcon}
            btnType={BUTTONTYPE.WITHOUT_BORDER}
            onClick={handleClose}
            disabled={isSubmitting}
          />
        </div>

        <form onSubmit={formik.handleSubmit} className={styles.form}>
          {error && <ErrorBlock error={error} />}

          <Input
            value={formik.values.accountName}
            label="Account name"
            onChange={formik.handleChange}
            name="accountName"
            error={formik.touched.accountName && formik.errors.accountName}
            disabled={isSubmitting || banksLoading || companiesLoading}
          />

          <Select
            label="Account bank"
            items={itemsBank}
            onSelect={handleBankSelect}
            selectedId={formik.values.accountBank?.id}
            placeholder={banksLoading ? "Loading banks..." : "Select bank"}
            disabled={isSubmitting || banksLoading}
            error={getError(
              formik.touched.accountBank,
              formik.errors.accountBank,
            )}
          />
          {banksError && <ErrorBlock error={banksError} />}

          <Select
            label="Account company"
            items={itemsCompany}
            onSelect={handleCompanySelect}
            selectedId={formik.values.accountCompany?.id}
            placeholder={
              companiesLoading ? "Loading companies..." : "Select company"
            }
            disabled={isSubmitting || companiesLoading || !hasBank}
            clearable
            error={getError(
              formik.touched.accountCompany,
              formik.errors.accountCompany,
            )}
          />
          {companiesError && <ErrorBlock error={companiesError} />}

          <Select
            label="Account type"
            items={bankTypes}
            onSelect={handleTypeSelect}
            selectedId={formik.values.accountType?.id}
            placeholder={
              !hasBank
                ? "Select bank first"
                : bankTypesLoading
                ? "Loading types..."
                : bankTypes.length === 0
                ? "No types available"
                : "Select type"
            }
            disabled={isSubmitting || !hasBank || bankTypesLoading || bankTypes.length === 0}
            error={getError(
              formik.touched.accountType,
              formik.errors.accountType,
            )}
          />

          <Input
            value={formik.values.accountNumber}
            label="Account number"
            onChange={formik.handleChange}
            name="accountNumber"
            error={formik.touched.accountNumber && formik.errors.accountNumber}
            disabled={isSubmitting}
          />

          <Button
            text={isSubmitting ? "Creating..." : "Add account"}
            type="submit"
            disabled={
              !formik.isValid ||
              !formik.dirty ||
              isSubmitting ||
              banksLoading ||
              companiesLoading
            }
          />
        </form>
      </div>
    </div>
  );
};
